/**
 * Shared class strings that make a Tiptap editor's *whole visible box* focusable.
 *
 * A Tiptap editor only takes focus where its contenteditable `.ProseMirror`
 * element actually is. Every composer in the app gives the editor a generous
 * `minHeight` and puts the padding on a wrapper `<div>` — so `.ProseMirror` is
 * only as tall as the document (one empty line), and a click in the top or
 * bottom of the box lands on an inert wrapper. The caret never appears and the
 * user has to hunt for the middle band. (Reported on the support ticket
 * Messages composer; the same shape existed in every rich-text editor.)
 *
 * The fix, applied identically everywhere:
 *
 * 1. The `minHeight` goes on the `EditorContent` element itself — not on a
 *    wrapper — so `[&_.ProseMirror]:min-h-[inherit]` resolves against it.
 * 2. ALL padding moves off the wrapper and onto `.ProseMirror`.
 *
 * The contenteditable then *is* the visible box, so any click inside it places
 * the caret. Tailwind's `box-sizing: border-box` keeps the min-height inclusive
 * of that padding, so nothing changes size.
 *
 * Padding is spelled out as literal constants because Tailwind's JIT cannot see
 * class names assembled at runtime.
 */

/** Makes `.ProseMirror` fill the `EditorContent` element's min-height. */
export const EDITOR_SURFACE_FILL = '[&_.ProseMirror]:min-h-[inherit]';

/** Compact composers and inline editors (was `px-3 py-2` on the wrapper). */
export const EDITOR_SURFACE_PADDING_SM = '[&_.ProseMirror]:px-3 [&_.ProseMirror]:py-2';

/** Medium editors (was `p-3` on the wrapper). */
export const EDITOR_SURFACE_PADDING_MD = '[&_.ProseMirror]:p-3';

/** Full-page editors (was `p-4` on the wrapper). */
export const EDITOR_SURFACE_PADDING_LG = '[&_.ProseMirror]:p-4';
